import { Injectable } from '@angular/core';
import { AbstractFeatureService } from '@y42/feature';
import { getJSONSchemaURL, METADATA_JSON_SCHEMA_URL, Y42MetadataGitModel } from '@y42/feature/json-schema';
import { GitQuery } from '@y42/git';
import { CustomTheme, Theme, ThemeData, ThemeType, Y42FeatureType } from '@y42/json-schema';
import { ToastService } from '@y42/toast';
import { getSafeString } from '@y42/util/safe-string';
import { v4 as uuid } from 'uuid';
import { VisualizationDashboardQuery } from '../../state/dashboard';
import {
  VisualizationThemeGitService,
  VisualizationThemeQuery,
  VisualizationThemeSection,
  VisualizationThemeState,
  VisualizationThemeStore,
  VisualizationThemeStoreModel,
} from '../../state/theme';

@Injectable({ providedIn: 'root' })
export class VisualizationThemeService extends AbstractFeatureService<VisualizationThemeState> {
  constructor(
    protected readonly query: VisualizationThemeQuery,
    protected readonly visualizationQuery: VisualizationDashboardQuery,
    protected readonly store: VisualizationThemeStore,
    protected readonly git: VisualizationThemeGitService,
    protected readonly toast: ToastService,
    protected readonly gitQuery: GitQuery,
  ) {
    super({
      featureType: Y42FeatureType.VisualizationTheme,
      store,
      query,
      userData: null,
      git,
      gitQuery,
      toast,
      childStores: [],
    });
  }

  async create(data: { title: string; theme: Theme; themeId?: string }) {
    const entity = this.createEntity(data);

    this.store.setActive(entity.$path);

    await this.store.pending;
  }

  update(_item: Partial<VisualizationThemeStoreModel>, _$path?: string): Promise<void> {
    throw new Error('Method not implemented.');
  }

  updateLocalThemeChanges(themeChanges: Partial<ThemeData>): ThemeData {
    this.store.update((st) => ({
      ui: {
        ...st.ui,
        localThemeChanges: {
          ...st.ui.localThemeChanges,
          ...themeChanges,
        },
      },
    }));

    return this.query.getActiveThemeWithLocalChanges()!;
  }

  setActiveCustomTheme(themeId: string) {
    this.store.setActive(themeId);

    this.discardLocalThemeChanges();

    return this.query.getActive()!;
  }

  setActiveBuiltInTheme(themeName: string) {
    let builtInTheme = this.query
      .getAll()
      .find((x) => x.settings.theme.type === 'BuiltIn' && x.settings.theme.themeName === themeName);

    if (!builtInTheme) {
      builtInTheme = this.createEntity({ title: themeName, theme: { themeName, type: ThemeType.BuiltIn } });
    }

    this.discardLocalThemeChanges();
    this.store.setActive(builtInTheme.$path);

    return builtInTheme;
  }

  saveLocalChanges() {
    const localThemeChanges = this.store.getValue().ui.localThemeChanges;

    this.store.updateActive((active) => ({
      settings: {
        ...active.settings,
        theme: {
          ...active.settings.theme,
          ...localThemeChanges,
        },
      },
    }));

    this.discardLocalThemeChanges();
  }

  async createNewTheme(themeName: string) {
    const theme: CustomTheme = {
      ...this.query.getActiveThemeWithLocalChanges()!,
      themeName,
      type: ThemeType.Custom,
    };

    const entity = await this.createEntity({ title: themeName, theme });

    this.store.setActive(entity.$path);

    this.discardLocalThemeChanges();
  }

  removeCustomTheme(themeId: string) {
    this.store.delete(themeId);
  }

  renameCustomTheme(themeId: string, newThemeName: string) {
    this.store.update(themeId, (themeModel) => ({
      ...themeModel,
      settings: {
        ...themeModel.settings,
        theme: {
          ...themeModel.settings.theme,
          themeName: newThemeName,
        },
      },
    }));
  }

  delete(_$path?: string): Promise<void> {
    throw new Error('Method not implemented.');
  }

  override setActive() {
    const activeDashboard = this.visualizationQuery.getActive();
    const themeNameOrThemeId = activeDashboard?.settings.theme;

    if (themeNameOrThemeId) {
      const theme = this.query.getEntity(themeNameOrThemeId) ?? this.query.getEntityByName(themeNameOrThemeId);

      if (theme && theme.$path !== this.query.getActive()?.$path) {
        this.discardLocalThemeChanges();
        this.store.setActive(theme.$path);
      }
    }
  }

  discardLocalThemeChanges() {
    this.store.update((store) => ({
      ui: {
        ...store.ui,
        localThemeChanges: {},
      },
    }));
  }

  setActiveSection(section: VisualizationThemeSection) {
    this.store.update((state) => ({ ui: { ...state.ui, activeSection: section } }));
  }

  private createEntity(data: { title: string; theme: Theme; themeId?: string }) {
    const { title, theme } = data;
    let themeId = data.themeId;

    if (!themeId) {
      themeId = `${getSafeString(title)}-${uuid()}`;
    }

    return this.store.create({
      ...new Y42MetadataGitModel(),
      $schema: METADATA_JSON_SCHEMA_URL,
      $name: themeId,
      name: themeId,
      settings: {
        $schema: getJSONSchemaURL('visualization-theme-settings'),
        theme,
      },
    });
  }
}
